import { SortOrder, User } from '@core/models';
import { sortObjects } from '@core/utils/functional';
import { ASC, DESC } from '@core/utils/sorting';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { findIndex } from 'lodash';
import {
  acceptEula, createOrUpdateUserSuccess,
  loadOrgUsers, loadOrgUsersSuccess,
  loadUserSuccess,
  loadUsersSuccess,
  setConnection, setSortOrder,
  updateUserSearchOption, updateUserSearchOptionSuccess
} from '../actions/user.actions';

export interface UsersState {
  sortOrder?: SortOrder;
  user?: User;
  users?: User[];
  orgUsers?: User[];
  eulaAcceptedTime?: string;
  tkn?: string;
  publicPaths?: string[];
}

const initialState: UsersState = {
  sortOrder: {
    key: 'firstName',
    direction: ASC
  },
  user: null,
  users: null,
  orgUsers: null,
  tkn: null,
  eulaAcceptedTime: null
};

export const usersReducer = createReducer(
  initialState,
  on(loadUserSuccess, (state, action) => ({ ...state, user: action.user })),
  on(setConnection, (state, action) => ({ ...state, tkn: action.usersState.tkn, publicPaths: action.usersState.publicPaths })),
  on(loadUsersSuccess, (state, action) => ({ ...state, users: sortObjects(action.users, state.sortOrder) })),
  on(loadOrgUsers, (state, action) => ({ ...state, payload: action.orgId })),
  on(loadOrgUsersSuccess, (state, action) => {
    const orgUsers = sortObjects(action.users, state.sortOrder);

    return Object.assign({}, state, {
      orgUsers
    } as UsersState);
  }),
  on(createOrUpdateUserSuccess, (state, action) => {
    let users = state.users || [];
    const userAction = action.userAction;
    const user = userAction.user;
    const index = findIndex(users, { userId: user.userId });

    if (index >= 0) {
      users = [...users.slice(0, index), user, ...users.slice(index + 1)];
    } else {
      users = [...users, user];
    }

    return Object.assign({}, state, {
      users
    } as UsersState);
  }),
  on(setSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.sortOrder.direction
    } as SortOrder;

    if (newSortOrder.key === state.sortOrder.key) {
      // same key, toggle sort direction
      newSortOrder.direction = state.sortOrder.direction === ASC ? DESC : ASC;
    } else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.sortOrder.direction;
    }

    const users = sortObjects(state.users, newSortOrder);
    const orgUsers = sortObjects(state.orgUsers, newSortOrder);

    return Object.assign({}, state, {
      users,
      orgUsers,
      sortOrder: newSortOrder
    } as UsersState);
  }),
  on(acceptEula, (state, action) => ({ ...state, user: action.userAction.user })),
  on(updateUserSearchOption, (state, action) => ({ ...state, user: action.userAction.user })),
  on(updateUserSearchOptionSuccess, (state, action) => ({ ...state, user: action.user }))
);

export const selectUsersState = createFeatureSelector<UsersState>('usersState');
export const selectCurrentUser = createSelector(selectUsersState, (state) => state.user);
export const selectCurrentUsers = createSelector(selectUsersState, (state) => state.users);
export const selectCurrentOrgUsers = createSelector(selectUsersState, (state) => state.orgUsers);